/* ============================================================================
   ct-gifting-form.js
   Form controller for sections/ct-gifting-form.liquid (V20 Phase 1, B2B).

   WHAT IT DOES
   ------------
   1. Reads ?enquiry= and pre-selects the request type.
   2. Fills the hidden source_url / utm_* inputs from the current URL.
   3. Validates client side, shows per-field errors, disables while submitting.
   4. POSTs the lead as flat JSON to the n8n intake webhook.
   5. On 200 {"ok":true,"catalog_url":"..."} reveals the download from the
      RESPONSE. The catalog URL is never written into the page by Liquid, so it
      is not in view-source before submit. That is the whole point of the gate:
      a URL present up front makes the form decorative.

   THE DEEP-LINK PARAMETER IS ?enquiry=, NEVER ?type=
   --------------------------------------------------
   CONTRACTS.md section 3b. `?type=` is a Shopify-reserved query parameter and
   survives canonicalisation, so ?type=bulk and ?type=gifting would become two
   indexable duplicates of one URL. `?enquiry=` is stripped by Shopify, so the
   trap disappears with no layout/theme.liquid edit.

   Only the six exact CONTRACTS.md section 1 values are accepted. There is no
   alias table and there must not be one: an alias is a private second taxonomy,
   and the whole reason the contract is frozen is that three consumers share one
   list. An unrecognised value leaves the select UNSET rather than falling back
   to 'other' - substituting a wrong-but-valid answer into a required field is
   worse than asking the visitor to answer it, because nothing downstream can
   tell the guess apart from a real choice.

   NAMESPACE
   ---------
   One global, `window.CtBulkGifting`. Two document-level listeners and no
   more: a one-shot DOMContentLoaded boot, attached only when the document is
   still parsing, and `shopify:section:load` for the theme editor, which
   filters on our own section container before doing anything. There is exactly
   ONE shopify:section:* listener - an earlier version of this comment claimed
   two, and there was never a `shopify:section:unload` handler because a
   re-render replaces the DOM and takes the old instance's listeners with it.
   Nothing here queries a shared selector.

   WHAT UNLOCKS THE FORM, AND WHY IT IS ONLY ONE THING
   ---------------------------------------------------
   Read the intake workflow before changing this. In
   everaw-n8n/marketing/b2b-lead-intake.json the chain after the row is
   written is `Insert Lead` -> Render Sheet Row + Email -> Read Sheet Header ->
   Assert Sheet Header -> Append Sheet Mirror -> Assert Sheet Mirror -> Send
   Acknowledgement -> `Respond 200`. SIX consecutive nodes sit between the
   insert and the response and every one of them is onError: stopWorkflow, by
   contract. A stopWorkflow before the Respond node means the webhook never
   answers 200 and n8n returns a 5xx instead. "Row written, no 200" is
   therefore the single most likely production failure, not an edge case.

   The server also does NOT block duplicates. `Assess Duplicate` flags a repeat
   inside 24h and lets it through, by design. So a resubmit after a lost 200
   creates a SECOND row for the same buyer, which Kanika then works twice.

   Hence one rule, and it is deliberately narrow:

     UNLOCK ONLY ON AN EXPLICIT 4xx CARRYING ok:false.

   That is the one answer that proves the server decided against the lead
   before storing it: `Respond 400` is reachable only from `Valid?` (validation
   failed) and `Email Rate OK?` (rate limited), and both sit UPSTREAM of
   `Insert Lead`. Nothing was written, so asking the visitor to fix and resend
   is safe and correct.

   Every other outcome - 5xx, a 2xx that is not ok:true, a 4xx without
   ok:false, a transport error, a timeout, an abort - is UNKNOWN. The browser
   cannot tell "never left the machine" from "stored, then the response was
   lost", and one of those two is a duplicate lead. Unknown therefore does not
   unlock and does not say "please try again". It says we may already have the
   details and offers WhatsApp, which is a staffed rail, so an uncertain web
   submit still ends in a conversation rather than in a second row.

   The cost is accepted with eyes open: when the connection genuinely never
   opened, we lock a form over a lead that does not exist. The WhatsApp offer
   is what pays for that, and it is why the panel is not optional decoration.

   BROWSER TARGET - AND WHY THERE IS NO fetch() BELOW
   --------------------------------------------------
   ES5 syntax on purpose, matching assets/everaw-chat.js. Roughly 78% of mobile
   sessions run inside the Instagram or Facebook in-app webview
   (docs/viewport-and-device-targets.md); those track the OS webview, not the
   user's browser, and are the oldest engines we actually serve.

   `window.fetch` and `Promise` are NOT ES5 and are absent on exactly those
   engines. The previous version called them behind this ES5 comment, so on an
   old webview the submit handler threw straight after setBusy(true) and the
   form froze: disabled button, "Sending...", no error, no recovery. This file
   uses XMLHttpRequest instead, which is universal, and `Element.closest` is
   walked by hand for the same reason.
   ========================================================================= */

(function () {
  'use strict';

  /* The six canonical values. CONTRACTS.md section 1. Do not add to this list
     here: three consumers (this form, the WhatsApp Flow, the bot classifier)
     share one taxonomy and a private extension in any of them is the bug the
     contract exists to prevent. */
  var REQUEST_TYPES = [
    'corporate_gifting',
    'bulk_order',
    'wholesale',
    'distributor',
    'white_label',
    'other'
  ];

  /* CONTRACTS.md section 3b. Read this name and no other. */
  var ENQUIRY_PARAM = 'enquiry';

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];

  /* Field name -> validation rule. Names are exactly CONTRACTS.md section 2. */
  var REQUIRED_FIELDS = ['name', 'email', 'phone', 'company', 'request_type'];

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* Two thresholds, and the difference between them is the whole point.

     SLOW_NOTICE_MS only changes what the page SAYS. It never re-enables the
     form and it never aborts the request. The old code treated 25s as a
     failure: it re-enabled the button and showed an error while the POST was
     still in flight, so a slow-but-successful submit produced a false error
     AND a second lead when the visitor pressed send again. A request that has
     left the browser may already be a row in Supabase; the only safe response
     to "no answer yet" is to keep the form locked.

     HARD_TIMEOUT_MS is XMLHttpRequest's own ceiling and it does abort. Aborting
     still proves nothing about the server, so it does not unlock either. */
  var SLOW_NOTICE_MS = 15000;
  var HARD_TIMEOUT_MS = 60000;

  /* ---------------------------------------------------------------- helpers */

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function param(name) {
    var search = window.location.search || '';
    var re = new RegExp('[?&]' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^&#]*)');
    var m = re.exec(search);
    if (!m) return '';
    try {
      return decodeURIComponent(m[1].replace(/\+/g, ' '));
    } catch (e) {
      return '';
    }
  }

  /* Exact match against the six, and nothing else. No trimming, no case
     folding, no hyphen/underscore swapping: every one of those is an alias
     rule wearing a different hat, and each one silently accepts a link that
     does not match the contract the bot and the Flow are also writing to.
     A malformed link should look malformed. */
  function canonicalEnquiry(raw) {
    if (typeof raw !== 'string' || !raw) return '';
    return REQUEST_TYPES.indexOf(raw) === -1 ? '' : raw;
  }

  function digitsOnly(s) {
    return String(s || '').replace(/[^0-9]/g, '');
  }

  function hasClass(node, cls) {
    var c = node && node.getAttribute ? node.getAttribute('class') : null;
    return !!c && (' ' + c + ' ').indexOf(' ' + cls + ' ') !== -1;
  }

  /* Element.closest is not ES5 and is missing on the same old webviews that
     lack fetch. The previous code guarded it with `field.closest ? ... : null`,
     which is not a fallback: on those engines it returned null and every
     validation message was silently swallowed. This walks up instead. */
  function fieldWrap(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      if (hasClass(node, 'ctgf__field')) return node;
      node = node.parentNode;
    }
    return null;
  }

  function setFieldError(field, message) {
    if (!field) return;
    var wrap = fieldWrap(field);
    if (!wrap) return;
    var slot = qs(wrap, '.ctgf__error');
    if (message) {
      if (wrap.classList) wrap.classList.add('is-invalid');
      field.setAttribute('aria-invalid', 'true');
      if (slot) slot.textContent = message;
    } else {
      if (wrap.classList) wrap.classList.remove('is-invalid');
      field.removeAttribute('aria-invalid');
      if (slot) slot.textContent = '';
    }
  }

  /* POST flat JSON. XMLHttpRequest, not fetch: see the header.

     `handlers.answered(status, body)` fires only when the server actually
     replied. `handlers.silent()` fires when it did not - abort, timeout or a
     transport error - and the caller must treat that as UNKNOWN, never as
     failure, because the request may already have been processed.

     Note the Content-Type: application/json still triggers a CORS preflight,
     exactly as the fetch version did. No change in what n8n must answer. */
  function postJson(url, payload, handlers) {
    var xhr = new XMLHttpRequest();
    var closed = false;

    var answered = function (status, body) {
      if (closed) return;
      closed = true;
      handlers.answered(status, body);
    };
    var silent = function () {
      if (closed) return;
      closed = true;
      handlers.silent();
    };

    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = HARD_TIMEOUT_MS;

    xhr.onload = function () {
      var body = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch (e) {
        body = null;
      }
      answered(xhr.status, body);
    };
    xhr.onerror = silent;
    xhr.ontimeout = silent;
    xhr.onabort = silent;

    try {
      xhr.send(JSON.stringify(payload));
    } catch (e) {
      silent();
    }
  }

  /* ------------------------------------------------------------ controller */

  function Ctgf(root) {
    this.root = root;
    this.form = qs(root, '[data-ctgf-form]');
    this.card = qs(root, '[data-ctgf-card]');
    this.success = qs(root, '[data-ctgf-success]');
    this.alert = qs(root, '[data-ctgf-alert]');
    this.submitBtn = qs(root, '[data-ctgf-submit]');
    this.submitLabel = qs(root, '[data-ctgf-submit-label]');
    this.downloadWrap = qs(root, '[data-ctgf-download-wrap]');
    this.download = qs(root, '[data-ctgf-download]');
    this.unresolvedPanel = qs(root, '[data-ctgf-unresolved]');
    this.endpoint = root.getAttribute('data-endpoint') || '';
    this.busy = false;
    this.done = false;

    this.copy = {
      required: root.getAttribute('data-msg-required') || 'This field is required.',
      email: root.getAttribute('data-msg-email') || 'Enter a valid email address.',
      phone: root.getAttribute('data-msg-phone') || 'Enter a valid phone number.',
      generic: root.getAttribute('data-msg-generic') || 'Something went wrong. Please try again.',
      /* Shown only on a 4xx with ok:false, the one answer that proves nothing
         was stored. This is the ONLY message in the file that may invite a
         resend. */
      rejected:
        root.getAttribute('data-msg-rejected') ||
        'Please check the details above and send again.',
      /* Shown when the outcome is unknown. Never worded as an invitation to
         retry: a retry here is what creates the duplicate row. */
      unresolved:
        root.getAttribute('data-msg-unresolved') ||
        'We could not confirm whether that reached us. Please do not send it again, because we may already have your details. Message us on WhatsApp and we will pick it up from there.',
      pending:
        root.getAttribute('data-msg-pending') ||
        'This is taking longer than usual. Your details have been sent, so please do not send them again. The catalog will reach your inbox shortly.',
      submit: (this.submitLabel && this.submitLabel.textContent) || 'Get the catalog',
      submitting: root.getAttribute('data-msg-submitting') || 'Sending...',
      sent: root.getAttribute('data-msg-sent') || 'Details sent'
    };
  }

  Ctgf.prototype.init = function () {
    if (!this.form || this.form.getAttribute('data-ctgf-ready') === '1') return;
    this.form.setAttribute('data-ctgf-ready', '1');

    this.prefillType();
    this.prefillContext();
    this.bind();
  };

  /* 1. ?enquiry= pre-selection. An unrecognised value leaves the select on its
        placeholder rather than guessing, so a mistyped or aliased link cannot
        silently mislabel a lead. */
  Ctgf.prototype.prefillType = function () {
    var select = this.form.elements.namedItem
      ? this.form.elements.namedItem('request_type')
      : qs(this.form, '[name="request_type"]');
    if (!select) return;
    var wanted = canonicalEnquiry(param(ENQUIRY_PARAM));
    if (!wanted) return;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === wanted) {
        select.selectedIndex = i;
        return;
      }
    }
  };

  /* 2. Context capture. Read from the CURRENT url only. There is deliberately
        no cross-page persistence: this script runs on this page alone, so a
        sessionStorage fallback could only ever be populated by a previous
        visit to this same page, which is not the case it would be solving. */
  Ctgf.prototype.prefillContext = function () {
    var self = this;
    var setHidden = function (name, value) {
      var el = qs(self.form, '[name="' + name + '"]');
      if (el) el.value = value || '';
    };

    var href = '';
    try {
      href = String(window.location.href || '');
    } catch (e) {
      href = '';
    }
    if (href.length > 900) href = href.slice(0, 900);

    setHidden('source_url', href);
    for (var i = 0; i < UTM_KEYS.length; i++) {
      var v = param(UTM_KEYS[i]);
      if (v.length > 200) v = v.slice(0, 200);
      setHidden(UTM_KEYS[i], v);
    }
  };

  Ctgf.prototype.bind = function () {
    var self = this;

    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submit();
    });

    /* Clear a field's error as soon as the visitor starts fixing it. Waiting
       for the next submit leaves a red field under text that is now correct. */
    qsa(this.form, '[name]').forEach(function (el) {
      var handler = function () {
        setFieldError(el, '');
      };
      el.addEventListener('input', handler);
      el.addEventListener('change', handler);
    });
  };

  Ctgf.prototype.collect = function () {
    var data = {};
    var els = qsa(this.form, '[name]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el.name) continue;
      data[el.name] = typeof el.value === 'string' ? el.value.replace(/^\s+|\s+$/g, '') : el.value;
    }
    return data;
  };

  Ctgf.prototype.validate = function (data) {
    var firstBad = null;
    var self = this;

    var mark = function (name, message) {
      var el = qs(self.form, '[name="' + name + '"]');
      setFieldError(el, message);
      if (message && !firstBad) firstBad = el;
    };

    REQUIRED_FIELDS.forEach(function (name) {
      mark(name, data[name] ? '' : self.copy.required);
    });

    if (data.email && !EMAIL_RE.test(data.email)) {
      mark('email', this.copy.email);
    }

    /* Phone: count digits rather than pattern-match a format. Indian mobiles
       are 10 digits, and visitors paste +91, 0 prefixes, spaces and hyphens.
       Rejecting on shape would fail more real numbers than fake ones. */
    if (data.phone) {
      var d = digitsOnly(data.phone);
      if (d.length < 10 || d.length > 15) mark('phone', this.copy.phone);
    }

    if (data.request_type && REQUEST_TYPES.indexOf(data.request_type) === -1) {
      mark('request_type', this.copy.required);
    }

    return firstBad;
  };

  /* `tone` is 'error' (default) or 'pending'. Pending is not a failure and must
     not be painted like one: the submit is still running and the visitor's job
     is to wait, not to retry. */
  Ctgf.prototype.showAlert = function (message, tone) {
    if (!this.alert) return;
    if (this.alert.classList) {
      if (tone === 'pending') this.alert.classList.add('ctgf__alert--pending');
      else this.alert.classList.remove('ctgf__alert--pending');
    }
    if (message) {
      this.alert.textContent = message;
      this.alert.removeAttribute('hidden');
    } else {
      this.alert.textContent = '';
      this.alert.setAttribute('hidden', 'hidden');
    }
  };

  Ctgf.prototype.setBusy = function (busy) {
    this.busy = busy;
    /* add/remove rather than the two-argument toggle: the `force` parameter is
       missing on the older webviews this file targets, where it degrades to a
       plain toggle and flips the class the wrong way on the second call. */
    if (this.card && this.card.classList) {
      if (busy) this.card.classList.add('is-submitting');
      else this.card.classList.remove('is-submitting');
    }
    if (this.submitBtn) {
      this.submitBtn.disabled = busy;
      this.submitBtn.setAttribute('aria-disabled', busy ? 'true' : 'false');
    }
    if (this.submitLabel) {
      this.submitLabel.textContent = busy ? this.copy.submitting : this.copy.submit;
    }
    this.form.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  /* Terminal state for an UNKNOWN outcome. Not a failure and not a success:
     the details may or may not be a row in Supabase, and only the server knows.

     It stops the spinner, because the request is over, but it leaves the form
     permanently locked - `done` is the same flag onSuccess sets, so submit()
     returns early exactly as it does after a real success. The fields are
     disabled too: a live-looking form under a "do not send this again" notice
     invites the retry the whole state exists to prevent.

     Nothing re-enables any of this. That is deliberate. The recovery path is
     WhatsApp, which the panel offers, not a second POST. */
  Ctgf.prototype.setUnresolved = function () {
    this.done = true;
    this.busy = false;

    if (this.card && this.card.classList) this.card.classList.remove('is-submitting');
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.setAttribute('aria-disabled', 'true');
    }
    if (this.submitLabel) this.submitLabel.textContent = this.copy.sent;
    this.form.setAttribute('aria-busy', 'false');

    qsa(this.form, '[name]').forEach(function (el) {
      el.disabled = true;
    });

    /* Pending tone, not error red. The submit is not the visitor's mistake and
       must not be painted as one. */
    this.showAlert(this.copy.unresolved, 'pending');
    if (this.unresolvedPanel) this.unresolvedPanel.removeAttribute('hidden');
  };

  Ctgf.prototype.submit = function () {
    if (this.busy || this.done) return;

    var data = this.collect();

    /* Honeypot. Filled means bot: stop locally and say nothing useful. The
       server checks `hp` again; this only saves the round trip. */
    if (data.hp) {
      this.showAlert(this.copy.generic);
      return;
    }

    this.showAlert('');

    var firstBad = this.validate(data);
    if (firstBad) {
      if (firstBad.focus) firstBad.focus();
      return;
    }

    if (!this.endpoint) {
      this.showAlert(this.copy.generic);
      return;
    }

    /* Exactly the CONTRACTS.md section 2 record, flat, plus hp. Built field by
       field rather than by spreading the form so a stray input added later
       cannot quietly widen the payload. */
    var payload = {
      name: data.name || '',
      email: data.email || '',
      phone: data.phone || '',
      company: data.company || '',
      request_type: data.request_type || '',
      quantity_estimate: data.quantity_estimate || '',
      message: data.message || '',
      source_channel: 'web',
      source_url: data.source_url || '',
      utm_source: data.utm_source || '',
      utm_medium: data.utm_medium || '',
      utm_campaign: data.utm_campaign || '',
      hp: data.hp || ''
    };

    this.setBusy(true);

    var self = this;
    /* Copy change only. The form stays disabled and this.busy stays true, so
       submit() cannot run a second time. There is no `slow` flag any more:
       nothing reads it now that a silent outcome is unknown whenever it
       arrives, and a flag with no reader is a false signal to the next
       person that timing still changes the decision. */
    var slowTimer = window.setTimeout(function () {
      self.showAlert(self.copy.pending, 'pending');
    }, SLOW_NOTICE_MS);

    postJson(this.endpoint, payload, {
      /* The server replied. WHAT it replied is what decides, not the fact of a
         reply: a 5xx is n8n telling us a stopWorkflow fired, and six of those
         sit after the row is already written. */
      answered: function (status, body) {
        window.clearTimeout(slowTimer);
        var b = body || {};

        if (status >= 200 && status < 300 && b.ok === true) {
          self.onSuccess(b.catalog_url);
          return;
        }

        /* THE ONLY UNLOCK. A 4xx carrying ok:false is `Respond 400`, which is
           reachable only from `Valid?` and `Email Rate OK?`, both upstream of
           `Insert Lead`. Nothing was stored, so a resend is safe.

           Both halves are required. A bare 4xx without ok:false is somebody
           else answering - a CDN 403, an edge 429, a 404 at a moved path - and
           that tells us nothing about whether n8n ran. */
        if (status >= 400 && status < 500 && b.ok === false) {
          self.setBusy(false);
          /* b.error is server text. Assigned with textContent, never innerHTML,
             so a hostile string renders as characters. */
          self.showAlert(typeof b.error === 'string' && b.error ? b.error : self.copy.rejected);
          return;
        }

        /* Everything else: 5xx, a 2xx that is not ok:true, a 4xx from somebody
           other than our own Respond node. Unknown, so locked. */
        self.setUnresolved();
      },

      /* No reply at all: transport error, hard timeout or abort. Unknown in
         every case, whether or not the slow notice had already appeared.

         An earlier version unlocked when the failure came in fast, on the
         theory that a quick error means the connection never opened. That is
         an assumption, not an observation. XMLHttpRequest reports a reset
         mid-flight and a DNS failure through the same `onerror`, with no field
         that separates them, so "fast" is not evidence that nothing was
         written. navigator.onLine does not rescue it either: a drop that
         happens AFTER the bytes leave reads as offline while the row is
         already in Supabase. Since the browser cannot distinguish them, the
         handler treats both as unknown and stops guessing. */
      silent: function () {
        window.clearTimeout(slowTimer);
        self.setUnresolved();
      }
    });
  };

  Ctgf.prototype.onSuccess = function (catalogUrl) {
    this.done = true;
    this.setBusy(false);
    this.showAlert('');

    /* Only http(s) is accepted. Without this an unexpected `javascript:` or
       `data:` value from the response would become a clickable link. */
    var safe = '';
    if (typeof catalogUrl === 'string' && /^https?:\/\//i.test(catalogUrl.replace(/^\s+|\s+$/g, ''))) {
      safe = catalogUrl.replace(/^\s+|\s+$/g, '');
    }

    /* The "open it below" line and the button are one unit and are revealed
       together. They used to disagree: the copy promised a download
       unconditionally while this function hid the button whenever the webhook
       answered without a catalog_url, so the visitor read "Open it below" with
       nothing below it. Both now live inside data-ctgf-download-wrap. */
    if (safe && this.download) {
      this.download.setAttribute('href', safe);
      this.download.setAttribute('target', '_blank');
      this.download.setAttribute('rel', 'noopener noreferrer');
      if (this.downloadWrap) this.downloadWrap.removeAttribute('hidden');
    } else if (this.downloadWrap) {
      /* Lead is stored and the catalog is on its way by email. Saying nothing
         about a download beats promising one that is not there. */
      this.downloadWrap.setAttribute('hidden', 'hidden');
    }

    /* The heading and the intro live OUTSIDE the card, so before this they
       survived the swap: the page went on saying "Tell us what you need" and
       "Fill this in and the catalog opens right here, and lands in your inbox
       too" directly above a panel announcing that both of those things had
       already happened. Marking the root complete lets the CSS retire both.

       2026-08-22: both copies used to promise a reply "within one working day",
       200px apart. That timeframe was never agreed -- it was cut from the
       acknowledgement email on 2026-08-19 for exactly that reason and survived
       here in two schema defaults and one live setting. Gone from all three. */
    this.root.classList.add('is-complete');

    if (this.card) this.card.setAttribute('hidden', 'hidden');
    if (this.success) {
      this.success.removeAttribute('hidden');
      this.success.setAttribute('tabindex', '-1');
      try {
        this.success.focus();
      } catch (e) {
        /* focus() on a freshly unhidden node throws in some older webviews;
           the panel is still visible and announced by role="status". */
      }
    }
  };

  /* ----------------------------------------------------------------- boot */

  function initAll(scope) {
    var root = scope || document;
    qsa(root, '[data-ctgf-root]').forEach(function (el) {
      new Ctgf(el).init();
    });
    if (root !== document && root.matches && root.matches('[data-ctgf-root]')) {
      new Ctgf(root).init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor. The ONLY document-level listener in this file. Re-rendering
     the section replaces the DOM, so the previous instance's listeners are gone
     with it and there is nothing to tear down on unload. Scoped to
     event.target, so this never touches another section's markup. */
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector) return;
    if (!event.target.querySelector('[data-ctgf-root]')) return;
    initAll(event.target);
  });

  window.CtBulkGifting = {
    init: initAll,
    requestTypes: REQUEST_TYPES.slice(),
    enquiryParam: ENQUIRY_PARAM
  };
})();
